import type { GoogleMapInfo, HomePage, MapInfo } from "@finn-no/mobility-dealer-types";
import { WIcon } from "@mobility-item-page/client";
import { ElementType } from "@mobility-item-page/utilities/client";
import { Button } from "@warp-ds/react";
import { type ReactNode, useState } from "react";
import { ObjectPosition } from "../../types/objectPosition.ts";
import { useValidDealerContext } from "../context/DealerProviderContext.tsx";
import { useTracking } from "../utils/pulseHelper.ts";
import { HomepageSurvey } from "./HomepageSurvey.tsx";
import { ScreenReaderSeparator } from "./ScreenReaderSeparator.tsx";

type HomepageLinkProps = {
  homepageSurvey?: boolean;
  fixed?: boolean;
  trackClick: () => void;
  homePage: HomePage;
};

type AddressLinkProps = {
  mapInfo: MapInfo;
  trackClick: (elementType: ElementType) => void;
  children?: ReactNode;
};

export const AddressLink = ({ mapInfo, trackClick, children }: AddressLinkProps) => {
  return (
    <>
      <div className="flex">
        <a
          className="flex gap-8"
          href={mapInfo.linkUrl}
          target="_blank"
          rel="noopener nofollow"
          onClick={() => trackClick(ElementType.Link)}
        >
          <WIcon name="PinMarker" className="shrink-0 mt-2 s-icon" />
          <ScreenReaderSeparator />
          <span>{mapInfo.linkTitle}</span>
        </a>
      </div>
      {children ? (
        <a
          className="rounded-8 block hover:no-underline focus:no-underline focus:focusable focus:rounded-8"
          href={mapInfo.linkUrl}
          target="_blank"
          rel="noopener nofollow"
          onClick={() => trackClick(ElementType.Map)}
        >
          {children}
        </a>
      ) : null}
    </>
  );
};

export const GoogleMapsLink = ({
  trackClick,
  mapInfo,
}: {
  trackClick: (url: string) => void;
  mapInfo: GoogleMapInfo;
}) => {
  const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapInfo.linkTitle)}`;
  return (
    <a className="flex gap-8" href={href} target="_blank" rel="noopener nofollow" onClick={() => trackClick(href)}>
      <WIcon name="PinMarker" className="shrink-0 mt-2 s-icon" />
      <ScreenReaderSeparator />
      <span>{mapInfo.linkTitle}</span>
    </a>
  );
};

// TODO: 2025-11-18 launch day hack, please remove ASAP
const transformBlocketUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;

  // Transform www.blocket.se/$storeName to www.blocket.se/butik/$storeName
  // https:// prefix is optional, $storeName must contain at least one letter
  const blocketPattern = /^(https?:\/\/)?www\.blocket\.se\/([^/]+)$/;
  const match = url.match(blocketPattern);

  if (match?.[2]) {
    const storeName = match[2];
    // Ensure storeName contains at least one letter
    if (/[a-zA-Z]/.test(storeName)) {
      const prefix = match[1] || "";
      return `${prefix}www.blocket.se/butik/${storeName}`;
    }
  }

  return url;
};

export const HomepageLink = ({ homePage, trackClick, homepageSurvey = false }: HomepageLinkProps) => {
  const [showHomepageSurvey, setShowHomepageSurvey] = useState(false);
  const transformedUrl = transformBlocketUrl(homePage.linkUrl);

  return (
    <>
      <a
        href={transformedUrl}
        onClick={() => {
          trackClick();
          setShowHomepageSurvey(true);
        }}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center"
      >
        <WIcon name="Link" className="s-icon" />
        <ScreenReaderSeparator />
        <span className="ml-8 mr-4">{homePage.linkTitle}</span>
        <ScreenReaderSeparator />
        <WIcon name="LinkExternal" />
      </a>
      <HomepageSurvey
        showModal={showHomepageSurvey}
        toggleModal={setShowHomepageSurvey}
        featureToggle={homepageSurvey}
      />
    </>
  );
};

export const OtherAdsLink = ({ trackClick }: { trackClick: () => void }) => {
  const {
    itemData: { similarAdsFrom },
  } = useValidDealerContext();
  return (
    <a href={similarAdsFrom.linkUrl} onClick={trackClick} className="flex gap-8">
      <WIcon name="Ads" className="shrink-0 mt-2 s-icon" />
      <ScreenReaderSeparator />
      <span>{similarAdsFrom.linkTitle}</span>
    </a>
  );
};

export const DealerBrandingPageButton = () => {
  const {
    organisationData: { dealerBrandingPage },
  } = useValidDealerContext();
  const { trackBrandingPage } = useTracking();

  if (!dealerBrandingPage) return null;

  return (
    <Button
      className="inline-flex min-w-[272px]"
      secondary
      href={dealerBrandingPage.linkUrl}
      onClick={() => trackBrandingPage(ObjectPosition.Overview)}
    >
      {dealerBrandingPage.linkTitle}
    </Button>
  );
};

export const DealerBrandingPageLink = () => {
  const {
    organisationData: { dealerBrandingPage },
    external: { brand },
  } = useValidDealerContext();
  const { trackBrandingPage } = useTracking();

  if (!dealerBrandingPage) return null;

  return (
    <a
      href={dealerBrandingPage.linkUrl}
      onClick={() => trackBrandingPage(ObjectPosition.InfoCard)}
      className="flex gap-8"
    >
      <WIcon name="Store" className="shrink-0 mt-2 s-icon" />
      <ScreenReaderSeparator />
      <span>
        {
          dealerBrandingPage
            ? dealerBrandingPage.linkTitle
            : `See dealer profile on ${brand}` /*TODO: replace with dealerBrandingPage.linkTitle from organisationData*/
        }
      </span>
    </a>
  );
};

export const OrgLookupLink = ({ trackClick }: { trackClick: () => void }) => {
  const {
    organisationData: { companyRegistration },
  } = useValidDealerContext();

  if (!companyRegistration) return null;

  return (
    <a
      className="flex items-center gap-4"
      target="_blank"
      rel="noopener noreferrer"
      href={companyRegistration.registrationNumberLinkUrl}
      onClick={() => trackClick()}
    >
      {companyRegistration.registrationNumberLinkTitle}
      <ScreenReaderSeparator />
      <WIcon name="LinkExternal" />
    </a>
  );
};
